import { Modal, Form, Button, Spinner, Badge } from 'react-bootstrap';
import { useEffect, useState } from 'react';
import { apiUrl } from '../../../config/config';
import Swal from "sweetalert2";
import moment from 'moment';
import { Card } from "../../../components/card/card.jsx";

const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
});

const initialFormState = {
    titleEvent: '',
    yearEvent: new Date().getFullYear(),
    dates: []
};

function EventDetailModal({ show, onClose, eventSelected, fetchData }) {
    const [event, setEvent] = useState(initialFormState);
    const [saving, setSaving] = useState(false);
    const [newDate, setNewDate] = useState('');

    const userEmail = JSON.parse(window.localStorage.getItem("userProfile")).email;

    useEffect(() => {
        if (eventSelected) {
            setEvent({
                idEvent: eventSelected.idEvent,
                titleEvent: eventSelected.titleEvent,
                yearEvent: eventSelected.yearEvent,
                dates: eventSelected.dates || []
            });
        } else {
            setEvent(initialFormState);
        }
    }, [eventSelected]);

    const handleAddDate = () => {
        if (!newDate) {
            Toast.fire({
                icon: 'warning',
                title: 'Por favor selecciona una fecha'
            });
            return;
        }

        // Verificar que la fecha no esté duplicada
        const dateExists = event.dates.some(d =>
            moment(d.dateEvent).format('YYYY-MM-DD') === newDate
        );

        if (dateExists) {
            Toast.fire({
                icon: 'warning',
                title: 'Esta fecha ya ha sido agregada'
            });
            return;
        }

        setEvent({
            ...event,
            dates: [...event.dates, { dateEvent: newDate }]
        });
        setNewDate('');
    };

    const handleRemoveDate = (index) => {
        const updatedDates = event.dates.filter((_, i) => i !== index);
        setEvent({ ...event, dates: updatedDates });
    };

    const handleSave = async () => {
        // Validaciones
        if (!event.titleEvent) {
            Toast.fire({
                icon: 'warning',
                title: 'El título del evento es obligatorio'
            });
            return;
        }

        if (!event.yearEvent) {
            Toast.fire({
                icon: 'warning',
                title: 'El año del evento es obligatorio'
            });
            return;
        }

        if (event.dates.length === 0) {
            Toast.fire({
                icon: 'warning',
                title: 'Debe agregar al menos una fecha para el evento'
            });
            return;
        }

        setSaving(true);

        try {
            const method = event.idEvent ? 'PUT' : 'POST';
            const endpoint = event.idEvent
                ? `${apiUrl}/events/${event.idEvent}`
                : `${apiUrl}/events`;

            // Preparar fechas en formato simple
            const dates = event.dates.map(d => d.dateEvent);

            const body = {
                titleEvent: event.titleEvent,
                yearEvent: event.yearEvent,
                dates: dates,
                ...(event.idEvent
                    ? { updatedBy: userEmail }
                    : { createdBy: userEmail }
                )
            };

            const response = await fetch(endpoint, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });

            const data = await response.json();

            if (data.ok) {
                Toast.fire({
                    icon: 'success',
                    title: `Evento ${event.idEvent ? 'actualizado' : 'creado'} con éxito`
                });
                await fetchData();
                onClose();
            } else {
                const { message } = data;
                Toast.fire({
                    icon: 'error',
                    title: `Error al ${event.idEvent ? 'actualizar' : 'crear'} el evento`,
                    text: Array.isArray(message) ? message.join('\n') : message
                });
            }
        } catch (error) {
            console.error('Error saving event:', error);
            Toast.fire({
                icon: 'error',
                title: 'Error de conexión'
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={onClose} size="lg" backdropClassName='custom-backdrop'>
            <div className='modal-content card shadow-lg'>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {event.idEvent ? 'Editar Evento' : 'Nuevo Evento'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Título del Evento</Form.Label>
                            <Form.Control
                                type="text"
                                value={event.titleEvent}
                                onChange={(e) => setEvent({ ...event, titleEvent: e.target.value })}
                                placeholder="Ej: Campeonato Nacional de Motocross"
                            />
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Form.Label>Año del Evento</Form.Label>
                            <Form.Control
                                type="number"
                                value={event.yearEvent}
                                onChange={(e) => setEvent({ ...event, yearEvent: parseInt(e.target.value) })}
                                min="2020"
                                max="2099"
                            />
                        </Form.Group>

                        <hr className="my-4" />

                        <h6 className="fw-bold mb-3">Fechas del Evento</h6>

                        {/* Agregar nueva fecha */}
                        <Card className="card border-primary mb-3">
                            <div className="card-body">
                                <h6 className="card-title mb-3">Agregar Fecha</h6>
                                <div className="row g-3">
                                    <div className="col-md-8">
                                        <Form.Control
                                            type="date"
                                            value={newDate}
                                            onChange={(e) => setNewDate(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <Button
                                            variant="primary"
                                            onClick={handleAddDate}
                                            className="w-100"
                                        >
                                            <i className="bi bi-plus-circle me-2"></i>
                                            Agregar
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {/* Lista de fechas */}
                        {event.dates.length > 0 ? (
                            <div className="table-responsive">
                                <table className="table table-hover text-nowrap">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Fecha</th>
                                            <th style={{ width: '100px' }}>Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {event.dates
                                            .sort((a, b) => new Date(a.dateEvent) - new Date(b.dateEvent))
                                            .map((date, index) => (
                                                <tr key={index}>
                                                    <td className="align-middle">{index + 1}</td>
                                                    <td className="align-middle">
                                                        <Badge bg="primary">
                                                            {moment(date.dateEvent).format('DD/MM/YYYY')}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => handleRemoveDate(index)}
                                                            title="Eliminar fecha"
                                                        >
                                                            <i className="fa fa-trash"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-4 text-muted">
                                <i className="fa fa-calendar" style={{ fontSize: '2rem' }}></i>
                                <p className="mb-0 mt-2">No hay fechas agregadas</p>
                                <small>Agrega al menos una fecha para el evento</small>
                            </div>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onClose} disabled={saving}>
                        Cancelar
                    </Button>
                    <Button variant="primary" onClick={handleSave} disabled={saving}>
                        {saving ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Guardando...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-save me-2"></i>
                                Guardar
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </div>
        </Modal>
    );
}

export default EventDetailModal;